import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from 'fronton-react';
import { css } from 'linaria';
import {
  Banner,
  HeaderGifStructure,
  HeaderSection,
  OtherSection,
  RootLayout,
  StagesSection,
} from 'common/components';
import { KAZAKHSTAN } from 'common/constants/region-codes';
import { useRegionIdSelector } from 'modules/model-json';
import { PromoBlock } from 'remotes/promo-block';
import { SpecialOffer } from 'remotes/special-offer';
import {
  BATHROOM_REPAIR_LANDING_NAME,
  BATHROOM_REPAIR_CDN_PATH,
  BATHROOM_REPAIR_ELEMENT_BLOCK,
  BATHROOM_REPAIR_PATH,
  BATHROOM_REPAIR_SIGN_UP_HREF,
} from './bathroom-repair-constants';
import {
  BathroomRepairDesignPanel,
  BathroomRepairLastProjects,
  BathroomRepairQuestionSection,
  BathroomRepairUtpCardSection,
} from './components';
import { BathroomRepairSizing } from './components/bathroom-repair-sizing';

const bathroomRepairHeaderContainer = css`
  && {
    & img {
      @media (--to-desktop) {
        transform: scale(1.3);
      }
    }
  }
`;

export const BathroomRepair: React.FC = () => {
  const regionId = useRegionIdSelector();
  const designSignHref = useMemo(() => `${BATHROOM_REPAIR_SIGN_UP_HREF}${regionId}`, [regionId]);
  const { t } = useTranslation();

  return (
    <RootLayout currentPage={BATHROOM_REPAIR_PATH}>
      <HeaderSection
        containerClassname={bathroomRepairHeaderContainer}
        bgColor="#089FB0"
        description={t('header.description')}
        img={
          {
            srcSet: `${BATHROOM_REPAIR_CDN_PATH}/header/header.gif/header4.gif`,
            alt: t('header.title'),
          } as HeaderGifStructure
        }
        title={t('header.title')}
        mediaType="gif"
        hasPattern={false}
      />
      <BathroomRepairDesignPanel designSignHref={designSignHref} />
      <Layout>
        <PromoBlock elementBlock={BATHROOM_REPAIR_ELEMENT_BLOCK} />
        <SpecialOffer elementBlock={BATHROOM_REPAIR_ELEMENT_BLOCK} />
        <BathroomRepairUtpCardSection regionId={regionId} />
        <BathroomRepairLastProjects />
        <StagesSection
          sectionTitle={t('stages-section.title')}
          stages={[
            {
              imageSrc: `${BATHROOM_REPAIR_CDN_PATH}/stages/1.jpg/1-01.jpg`,
              imageAlt: t('stages-section.first-stage.image-alt'),
              name: t('stages-section.first-stage.title'),
              text: t('stages-section.first-stage.description'),
            },
            {
              imageSrc: `${BATHROOM_REPAIR_CDN_PATH}/stages/2.jpg/2.jpg`,
              imageAlt: t('stages-section.second-stage.image-alt'),
              name: t('stages-section.second-stage.title'),
              text: t('stages-section.second-stage.description'),
            },
            {
              imageSrc: `${BATHROOM_REPAIR_CDN_PATH}/stages/3.jpg/3.jpg`,
              imageAlt: t('stages-section.third-stage.image-alt'),
              name: t('stages-section.third-stage.title'),
              text: t('stages-section.third-stage.description'),
            },
            {
              imageSrc: `${BATHROOM_REPAIR_CDN_PATH}/stages/4.jpg/4.jpg`,
              imageAlt: t('stages-section.fourth-stage.image-alt'),
              name: t('stages-section.fourth-stage.title'),
              text: t('stages-section.fourth-stage.description'),
            },
            {
              imageSrc: `${BATHROOM_REPAIR_CDN_PATH}/stages/5.jpg/5.jpg`,
              imageAlt: t('stages-section.fifth-stage.image-alt'),
              name: t('stages-section.fifth-stage.title'),
              text: t('stages-section.fifth-stage.description'),
            },
            {
              imageSrc: `${BATHROOM_REPAIR_CDN_PATH}/stages/6.jpg/6.jpg`,
              imageAlt: t('stages-section.sixth-stage.image-alt'),
              name: t('stages-section.sixth-stage.title'),
              text: t('stages-section.sixth-stage.description'),
            },
          ]}
        />
        <Banner
          bgColor="#438286"
          elementBlock={BATHROOM_REPAIR_ELEMENT_BLOCK}
          content={{
            media: {
              alt: t('banner.img-alt') || '',
              srcD: `${BATHROOM_REPAIR_CDN_PATH}/banner/bathroom-d.jpg/bathroom-d.jpg`,
              srcT: `${BATHROOM_REPAIR_CDN_PATH}/banner/bathroom-t.jpg/bathroom-t.jpg`,
              src: `${BATHROOM_REPAIR_CDN_PATH}/banner/bathroom-m.png/bathroom-m.png`,
            },
            title: t('banner.title'),
            text: t('banner.text'),
          }}
          link={{
            href: '/uslugi/proektirovanie-i-dizayn/sozdayte-vannuyu-komnatu-svoey-mechty/',
            title: t('banner.link-title'),
          }}
          promotion={{
            name: BATHROOM_REPAIR_ELEMENT_BLOCK,
            creative: t('banner.title'),
            position: '1',
          }}
        />
        {regionId !== KAZAKHSTAN && <BathroomRepairSizing />}
        <BathroomRepairQuestionSection designSignHref={designSignHref} />
      </Layout>
      <OtherSection
        elementBlock={`${BATHROOM_REPAIR_ELEMENT_BLOCK} / other`}
        landingName={BATHROOM_REPAIR_LANDING_NAME}
        promotionName={`${BATHROOM_REPAIR_ELEMENT_BLOCK}_bottom`}
      />
    </RootLayout>
  );
};
